import { useMutation, useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import BooleanField from '../../core/components/Fields/BooleanField.tsx';
import FormField from '../../core/components/FormField.tsx';
import Layout from '../../core/components/Layout.tsx';
import Loader from '../../core/components/Loader.tsx';
import Page from '../../core/components/Page';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import SettingsNavigation from '../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import countFieldErrors from '../../core/utils/countFieldErrors.ts';
import { GET_COMPANY_DETAILS, UPDATE_TRAINING_DATA_SHARING_ENABLED } from '../queries.ts';

interface CustomerDataUsageFormValues {
    trainingDataSharingEnabled: boolean;
}

const CUSTOMER_DATA_USAGE_FORM_DEFAULT_VALUES: CustomerDataUsageFormValues = {
    trainingDataSharingEnabled: true,
};

const CustomerDataUsagePage = () => {
    const { t } = useTranslation('customer');
    const { publishToast } = useToaster();

    const form = useForm({ defaultValues: CUSTOMER_DATA_USAGE_FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    const { data } = useQuery(GET_COMPANY_DETAILS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        variables: {},
    });
    const customer = data?.viewer?.customer;
    const [formReady, setFormReady] = useState(false);

    useEffect(() => {
        if (customer) {
            form.reset({
                ...CUSTOMER_DATA_USAGE_FORM_DEFAULT_VALUES,
                trainingDataSharingEnabled: customer?.trainingDataSharingEnabled ?? true,
            });
            setFormReady(true);
        }
    }, [customer, form]);

    const [updateCustomer] = useMutation(UPDATE_TRAINING_DATA_SHARING_ENABLED);
    const handleUpdateCustomer = async (formData: CustomerDataUsageFormValues) => {
        try {
            await updateCustomer({
                variables: { input: { trainingDataSharingEnabled: formData.trainingDataSharingEnabled } },
            });
            publishToast({ description: t('dataUsage.form.updateSuccess'), status: 'success' });
            form.reset(formData);
        } catch (e) {
            console.error(e);
            form.reset();
            publishToast({ description: t('dataUsage.form.updateError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <Page.HeaderTitle>{t('dataUsage.title')}</Page.HeaderTitle>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    {!formReady ? (
                        <Loader />
                    ) : (
                        <SettingsFormCard onSubmit={form.handleSubmit(handleUpdateCustomer)}>
                            <Controller
                                name="trainingDataSharingEnabled"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('dataUsage.form.trainingDataSharingEnabled')}
                                        error={formErrors.trainingDataSharingEnabled}
                                        helpText={t('dataUsage.form.trainingDataSharingEnabledHelpText')}
                                    >
                                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                                    </FormField>
                                )}
                            />

                            <SaveStatus
                                isDirty={form.formState.isDirty}
                                isSubmitting={form.formState.isSubmitting}
                                errorCount={countFieldErrors(formErrors)}
                            />
                        </SettingsFormCard>
                    )}
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default CustomerDataUsagePage;
